import { Position, useEdges, useReactFlow, useUpdateNodeInternals } from '@xyflow/react';
import { useEffect, useState } from 'react';
import { useTrashNode } from './commons/trashNode';
import { FlowHandle } from './commons/flowHandle';
import './style.scss';
import { UilTrashAlt } from '@iconscout/react-unicons';
import SimpleLabelTextBox from 'pub-components/ui-controls/ui-components/SimpleLabelTextbox';
import { Button } from 'react-bootstrap';
import { handleClipboardKeyDown } from '../helper';

/**
 * Silent Paths/Filter node. Each transition is a condition evaluated against
 * workflow facts / latest user message; use "otherwise" as the catch-all path.
 */
export default function ConditionNode({ id, data, editable = true }) {
  const { setNodes, setEdges } = useReactFlow();
  const updateNodeInternals = useUpdateNodeInternals();
  const trashNode = useTrashNode();
  const edges = useEdges();

  const [items, setItems] = useState(
    data?.transitions?.length
      ? data.transitions
      : [
          { id: `transition-${id}-0`, value: '' },
          { id: `transition-${id}-1`, value: 'otherwise' }
        ]
  );

  const onChange = newData => {
    setNodes(nds =>
      nds.map(node =>
        node.id === id ? { ...node, data: { ...node.data, ...newData } } : node
      )
    );
  };

  const IsValidConnection = connection => {
    if (edges.some(edge => edge.sourceHandle === connection.sourceHandle))
      return false;
    return true;
  };

  const addItem = () => {
    setItems(prev => {
      const maxIndex = prev.reduce((max, item) => {
        const match = item.id.match(new RegExp(`^transition-${id}-(\\d+)$`));
        if (!match) return max;
        const num = parseInt(match[1], 10);
        return Number.isNaN(num) ? max : Math.max(max, num);
      }, -1);

      const next = [
        ...prev,
        { id: `transition-${id}-${maxIndex + 1}`, value: '' }
      ];

      onChange({ transitions: next });
      return next;
    });
  };

  const updateItem = (index, value) => {
    setItems(prev => {
      const next = prev.map((item, i) =>
        i === index ? { ...item, value } : item
      );
      onChange({ transitions: next });
      return next;
    });
  };

  const removeItem = index => {
    setItems(prev => {
      const deleted = prev[index];
      const next = prev.filter((_, i) => i !== index);

      setEdges(eds =>
        eds.filter(
          e => e.sourceHandle !== deleted.id && e.targetHandle !== deleted.id
        )
      );

      onChange({ transitions: next });
      return next;
    });
  };

  useEffect(() => {
    if (!data?.transitions || data.transitions.length === 0) {
      onChange({ transitions: items });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    updateNodeInternals(id);
  }, [id, items, updateNodeInternals]);

  return (
    <dl className="mt-3" style={{ width: '290px' }}>
      <FlowHandle
        type="target"
        position={Position.Left}
        id={`${id}-target`}
        editable={editable}
        isConnectable={true}
      />

      <div
        className="card shadow-sm border-gray rounded-10"
        style={{ backgroundColor: '#F9F9F9' }}
      >
        <div
          className="d-flex align-items-center px-4 node-header"
          style={{ backgroundColor: '#FFF4E5' }}
        >
          <div className="d-flex mr-auto ml-2">
            <dt
              className="text-truncate fw-semibold fs-6"
              style={{ color: '#9A5B00' }}
            >
              Condition Node
            </dt>
          </div>

          <div className="ms-auto">
            <Button
              variant="transparent"
              onClick={e => trashNode(e, id)}
              className="d-flex align-items-center rounded-circle p-2 header-delete-icon"
              title="Delete condition node"
              disabled={!editable}
            >
              <UilTrashAlt
                style={{ width: '24px', height: '24px', color: '#dc3545' }}
              />
            </Button>
          </div>
        </div>

        <div className="card-body p-2">
          <div className="px-4 pb-2">
            <div
              className="small px-2 py-2 rounded"
              style={{ backgroundColor: '#FFFFFF', color: '#4B5563' }}
            >
              Silent filter/paths node. Routes by matching conditions to facts
              or the latest user message. Use <strong>otherwise</strong> as the
              fallback path.
            </div>
          </div>

          <div className="px-4 pb-3" style={{ marginBottom: '10px' }}>
            {items.length > 0 && (
              <div>
                <dt className="text-truncate small fw-medium mt-3 px-4 label-text">
                  Condition
                </dt>
                <div>
                  {items.map((item, index) => (
                    <div
                      key={item.id}
                      className="mt-1 d-flex align-items-center gap-4 position-relative fw-semibold mb-2"
                    >
                      <FlowHandle
                        type="source"
                        position={Position.Right}
                        id={item.id}
                        style={{
                          right: '-13px',
                          top: '50%'
                        }}
                        editable={editable}
                        isValidConnection={IsValidConnection}
                      />
                      <SimpleLabelTextBox
                        dataProperty={item.value}
                        editable={editable}
                        handleChange={e => updateItem(index, e.target.value)}
                        onkeyPress={handleClipboardKeyDown}
                        placeholder="e.g. Fees, Question, otherwise"
                        inputStyles={{ backgroundColor: 'white' }}
                        inputPropStyle={{ padding: '10px' }}
                        styles={{ flex: 1 }}
                      />
                      {!/^otherwise$/i.test((item.value || '').trim()) && (
                        <Button
                        variant="transparent"
                        type="button"
                        className="btn btn-sm btn-link text-danger ms-2 p-0 ml-1"
                        onClick={() => removeItem(index)}
                        title="Remove"
                        disabled={!editable}
                      >
                        <UilTrashAlt
                          style={{
                            width: '20px',
                            height: '20px',
                            color: '#dc3545'
                          }}
                        />
                      </Button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
            <button
              type="button"
              onClick={addItem}
              className="mt-3 fw-semibold align-middle border-0 rounded-1 shadow-sm condition-button p-1"
              disabled={!editable}
            >
              + Add Condition
            </button>
          </div>
        </div>
      </div>
    </dl>
  );
}
